<a class="bc-skip-link" href="#main" (click)="focusMain($event)">Skip to main content</a>

<!-- phone / tablet: docs/redesign/mobile. Desktop (>= 1280) carries neither class and is unchanged. -->
<div class="shell" [class.rail]="isRail" [class.rail-labels]="railLabels" [class.bare]="!loginDisplay || isOnboarding || isChatPage"
     [class.nav-open]="isMobileMenuOpen" [class.phone]="layout.isPhone()" [class.tablet]="layout.useRail()"
     [class.has-tabs]="showTabBar" [class.immersive]="phoneImmersive">

  <!-- ============ Sidebar ============ -->
  <!-- Not on Notes opened in its own tab: there it works as a chat window.
       Phone: a drawer (a modal dialog while open). Tablet: the rail, or the full sidebar over the page. -->
  <nav *ngIf="loginDisplay && !isOnboarding && !isChatPage" class="sidebar not-printable" aria-label="Main"
       [attr.role]="overlayNav ? 'dialog' : null" [attr.aria-modal]="overlayNav ? 'true' : null"
       [cdkTrapFocus]="overlayNav">
    <div class="brand-row">
      <bc-app-switcher [apps]="apps" [currentAppId]="currentAppId" [rail]="isRail" [sheet]="layout.isPhone()"
                       (openApp)="onOpenApp($event)"></bc-app-switcher>
      <button *ngIf="layout.isPhone(); else railToggle" type="button" class="drawer-close" aria-label="Close menu"
              (click)="closeMobileNav()">
        <mat-icon aria-hidden="true">close</mat-icon>
      </button>
      <ng-template #railToggle>
        <button type="button" class="collapse" (click)="toggleRail()"
                [attr.aria-label]="isRail ? 'Expand sidebar' : 'Collapse sidebar'" [attr.aria-expanded]="!isRail">
          <mat-icon aria-hidden="true">{{ isRail ? 'chevron_right' : 'chevron_left' }}</mat-icon>
        </button>
      </ng-template>
    </div>

    <!-- Tablet rail: Create stays one tap away (mockups/tablet). -->
    <button *ngIf="railLabels && createItems.length" type="button" class="rail-create" [matMenuTriggerFor]="createMenu"
            aria-label="Create new">
      <mat-icon aria-hidden="true">add</mat-icon>
    </button>

    <div class="org-row" *ngIf="!isRail">
      <button type="button" class="org" [matMenuTriggerFor]="companyMenu" #companyMenuTrigger="matMenuTrigger"
              (menuOpened)="onCompanyMenuOpened()"
              [attr.aria-label]="'Switch organisation, current: ' + (currentCompanyName || 'none')">
        <mat-icon aria-hidden="true">business</mat-icon>
        <span class="org-name">{{ currentCompanyName || 'Organisation' }}</span>
        <mat-icon aria-hidden="true">unfold_more</mat-icon>
      </button>
      <button *ngIf="createItems.length" type="button" class="create" [matMenuTriggerFor]="createMenu" aria-label="Create new">
        <mat-icon aria-hidden="true">add</mat-icon>
      </button>
    </div>

    <ul class="modules">
      <li *ngFor="let item of visibleNavItems">
        <!-- A page of its own: a real link, so Ctrl/⌘/middle-click still open a new tab. -->
        <a *ngIf="!item.children" class="nav-link" [href]="buildNavHref(item)" (click)="onNavItemClick(item, $event)"
           [class.active]="isNavItemActive(item)" [attr.aria-current]="isNavItemActive(item) ? 'page' : null"
           [attr.aria-label]="isRail ? item.name : null"
           [matTooltip]="isRail ? item.name : ''" matTooltipPosition="right">
          <mat-icon aria-hidden="true">{{ item.icon }}</mat-icon>
          <span *ngIf="railLabels" class="rail-label" aria-hidden="true">{{ item.name }}</span>
          <span *ngIf="!isRail" class="label">{{ item.name }}</span>
          <span *ngIf="!isRail && item.notesOnly && notesUnread" class="nav-badge">
            {{ notesUnread }}<span class="bc-sr-only"> unread</span>
          </span>
        </a>

        <!-- A section: expands to its pages. Only the active one is open by default. -->
        <ng-container *ngIf="item.children">
          <button type="button" class="nav-link" (click)="toggleSection(item)"
                  [class.active]="isNavItemActive(item)" [attr.aria-expanded]="!isRail && isExpanded(item)"
                  [attr.aria-label]="isRail ? item.name : null"
                  [matTooltip]="isRail ? item.name : ''" matTooltipPosition="right">
            <mat-icon aria-hidden="true">{{ item.icon }}</mat-icon>
            <span *ngIf="railLabels" class="rail-label" aria-hidden="true">{{ item.name }}</span>
            <ng-container *ngIf="!isRail">
              <span class="label">{{ item.name }}</span>
              <mat-icon class="chevron" aria-hidden="true">{{ isExpanded(item) ? 'expand_less' : 'expand_more' }}</mat-icon>
            </ng-container>
          </button>
          <ul *ngIf="!isRail && isExpanded(item)" class="children">
            <li *ngFor="let child of item.children">
              <a class="child-link" [href]="buildNavHref(child)" (click)="onNavChildClick(child, $event)"
                 [class.active]="isChildActive(child)" [attr.aria-current]="isChildActive(child) ? 'page' : null">
                {{ child.name }}
                <span *ngIf="badgeFor(child) as count" class="nav-badge">
                  {{ count }}<span class="bc-sr-only"> need action</span>
                </span>
              </a>
            </li>
          </ul>
        </ng-container>
      </li>
    </ul>

    <div class="account">
      <button type="button" class="account-btn" [matMenuTriggerFor]="accountMenu"
              [attr.aria-label]="'Account and settings' + (userDisplayName ? ', ' + userDisplayName : '')">
        <span class="avatar" aria-hidden="true">{{ userInitials }}</span>
        <span *ngIf="!isRail" class="who" aria-hidden="true">
          <span class="who-name">{{ userDisplayName }}</span>
          <span class="who-sub">Settings &amp; account</span>
        </span>
      </button>
    </div>
  </nav>

  <div *ngIf="overlayNav" class="nav-scrim" (click)="closeMobileNav()" aria-hidden="true"></div>

  <!-- ============ Main column ============ -->
  <div class="main-col">
    <header *ngIf="loginDisplay && !isOnboarding" class="topbar not-printable"
            [class.searching]="phoneSearchOpen" [class.tools-open]="phoneToolsOpen">
      <!-- Phone only (CSS): opens the drawer. -->
      <button *ngIf="!isChatPage && !phoneSearchOpen" type="button" class="menu-toggle" (click)="toggleMobileNav($event)"
              aria-haspopup="dialog" [attr.aria-label]="isMobileMenuOpen ? 'Close menu' : 'Open menu'" [attr.aria-expanded]="isMobileMenuOpen">
        <mat-icon aria-hidden="true">{{ isMobileMenuOpen ? 'close' : 'menu' }}</mat-icon>
      </button>
      <button *ngIf="phoneSearchOpen" type="button" class="appbar-btn" aria-label="Close search" (click)="closePhoneSearch()">
        <mat-icon aria-hidden="true">arrow_back</mat-icon>
      </button>

      <!-- Phone app bar title (mockups/phone): the page, and this tab's organisation under it. -->
      <div *ngIf="layout.isPhone() && !phoneSearchOpen" class="appbar-title">
        <span class="appbar-page">{{ pageTitle }}</span>
        <span *ngIf="currentCompanyName" class="appbar-sub">{{ currentCompanyName }}</span>
      </div>

      <nav class="crumb" aria-label="Breadcrumb">
        <ol>
          <li *ngFor="let part of breadcrumb; let first = first; let last = last" [attr.aria-current]="last ? 'page' : null">
            <a *ngIf="first; else crumbText" class="crumb-link" [href]="appHomeHref" (click)="goToAppHome($event)">{{ part }}</a>
            <ng-template #crumbText>{{ part }}</ng-template>
          </li>
        </ol>
      </nav>

      <!-- Global search: the header's own search, scope filters and results, restyled. -->
      <div class="search" #searchContainer>
        <label class="bc-sr-only" for="bc-global-search">Search or jump to</label>
        <div class="search-box">
          <mat-icon aria-hidden="true">search</mat-icon>
          <input id="bc-global-search" #searchInput type="text" [formControl]="searchControl"
                 [placeholder]="getSearchPlaceholder()" autocomplete="off"
                 role="combobox" aria-controls="global-search-results" aria-keyshortcuts="Control+K"
                 [attr.aria-expanded]="showSearchResults"
                 [attr.aria-activedescendant]="activeResultIndex >= 0 ? 'global-search-result-' + activeResultIndex : null"
                 (focus)="onSearchFocus()" (blur)="onSearchBlur()" (keydown)="onSearchKeydown($event)">
          <mat-spinner *ngIf="isSearching" diameter="18" aria-label="Searching"></mat-spinner>
          <button *ngIf="searchControl.value || activeSearchFilters.length || selectedSearchCompanyIds.length"
                  type="button" class="clear" aria-label="Clear search" (click)="clearSearch()">
            <mat-icon aria-hidden="true">close</mat-icon>
          </button>
          <kbd *ngIf="!searchControl.value" aria-hidden="true">Ctrl K</kbd>
        </div>

        <div class="results" *ngIf="showSearchFilters || isSearching || (showSearchResults && searchResults.length > 0) || (showSearchResults && searchResults.length === 0 && searchControl.value?.length >= 2)">
          <div class="scope" *ngIf="showSearchFilters || activeSearchFilters.length > 0 || selectedSearchCompanyIds.length > 0"
               role="group" aria-label="Search in">
            <span class="scope-label" aria-hidden="true">Search in</span>
            <button *ngFor="let f of searchFilterOptions" type="button" class="scope-chip"
                    [attr.aria-pressed]="isFilterActive(f.value)"
                    (mousedown)="toggleSearchFilter(f.value); $event.preventDefault()">
              <mat-icon aria-hidden="true">{{ f.icon }}</mat-icon>{{ f.label }}
            </button>
            <button type="button" class="scope-chip" [attr.aria-pressed]="selectedSearchCompanyIds.length > 0"
                    [matMenuTriggerFor]="companiesScopeMenu"
                    (menuOpened)="searchCompaniesMenuOpen = true" (menuClosed)="onSearchCompaniesMenuClosed()"
                    (mousedown)="$event.preventDefault()">
              <mat-icon aria-hidden="true">business</mat-icon>{{ searchCompanyButtonLabel }}
              <mat-icon aria-hidden="true">expand_more</mat-icon>
            </button>
          </div>

          <mat-progress-bar *ngIf="isSearching" mode="indeterminate" aria-label="Searching"></mat-progress-bar>

          <p class="degraded" *ngIf="!isSearching && unavailableModules.length > 0" role="status">
            <mat-icon aria-hidden="true">cloud_off</mat-icon>
            Some results may be missing: {{ unavailableModules[0].label }} ({{ unavailableModules[0].reason | lowercase }})<span
              *ngIf="unavailableModules.length > 1">, +{{ unavailableModules.length - 1 }} more</span>
          </p>

          <!-- Shown while later modules are still answering: results fill in module by module. -->
          <div id="global-search-results" role="listbox" aria-label="Search results" *ngIf="searchResults.length > 0">
            <ng-container *ngFor="let group of searchResults">
              <div class="group-head" role="presentation">{{ group.category }}</div>
              <div *ngFor="let result of group.results" class="result" role="option"
                   [id]="'global-search-result-' + flatResults.indexOf(result)"
                   [attr.data-result-index]="flatResults.indexOf(result)"
                   [attr.aria-selected]="isResultActive(result)" [class.active]="isResultActive(result)"
                   (mousedown)="onSearchResultClick(result)">
                <mat-icon aria-hidden="true">{{ result.icon }}</mat-icon>
                <span class="result-text">
                  <span class="result-title">{{ result.title }}</span>
                  <span class="result-sub">{{ result.subtitle }}</span>
                </span>
                <span class="result-org" *ngIf="result.availableInCompanies?.length"
                      [matTooltip]="'Available in: ' + result.availableInCompanies!.join(', ')">
                  {{ result.availableInCompanies![0] }}<span *ngIf="result.availableInCompanies!.length > 1"> +{{ result.availableInCompanies!.length - 1 }}</span>
                </span>
                <span class="result-org" *ngIf="!result.availableInCompanies?.length && result.companyName && result.companyId !== managedCompanyId"
                      [class.result-org--restricted]="result.restricted"
                      [matTooltip]="result.restricted ? 'You have no access in ' + result.companyName : ''">
                  <mat-icon *ngIf="result.restricted" aria-hidden="true">lock</mat-icon>{{ result.companyName }}
                </span>
                <!-- mousedown, like the row: the input's blur closes the list before a click lands. -->
                <button *ngIf="canRequestShare(result)" type="button" class="result-share"
                        [attr.aria-disabled]="shareRequestedFormIds.has('' + result.id)"
                        [class.done]="shareRequestedFormIds.has('' + result.id)"
                        [matTooltip]="'Ask the owner to share this client with ' + currentCompanyName"
                        [attr.aria-label]="'Request share of ' + result.title + ' into ' + currentCompanyName"
                        (mousedown)="requestShareFromSearch(result, $event)">
                  <mat-icon aria-hidden="true">{{ shareRequestedFormIds.has('' + result.id) ? 'check' : 'share' }}</mat-icon>
                  {{ shareRequestedFormIds.has('' + result.id) ? 'Requested' : 'Request share' }}
                </button>
              </div>
            </ng-container>
          </div>

          <p class="empty" *ngIf="!isSearching && searchResults.length === 0 && searchControl.value?.length >= 2" role="status">
            No results for “{{ searchControl.value }}”.
          </p>
        </div>
      </div>

      <!-- This tab's organisation. Admins of it get the Company Settings shortcuts; others only see the name. -->
      <ng-container *ngIf="currentCompanyName">
        <button *ngIf="canOpenCompanySettings; else orgName" type="button" class="topbar-org"
                [matMenuTriggerFor]="orgSettingsMenu" matTooltip="Company settings"
                [attr.aria-label]="'Organisation: ' + currentCompanyName + '. Company settings'">
          <mat-icon aria-hidden="true">business</mat-icon>
          <span class="topbar-org-name">{{ currentCompanyName }}</span>
          <mat-icon aria-hidden="true">expand_more</mat-icon>
        </button>
        <ng-template #orgName>
          <span class="topbar-org topbar-org--static" [attr.aria-label]="'Organisation: ' + currentCompanyName">
            <mat-icon aria-hidden="true">business</mat-icon>
            <span class="topbar-org-name">{{ currentCompanyName }}</span>
          </span>
        </ng-template>
      </ng-container>

      <!-- Phone: search is an icon until tapped, then it takes the whole bar. -->
      <button *ngIf="layout.isPhone() && !phoneSearchOpen" type="button" class="appbar-btn" aria-label="Search"
              aria-keyshortcuts="Control+K" (click)="openPhoneSearch()">
        <mat-icon aria-hidden="true">search</mat-icon>
      </button>

      <div class="tools">
        <!-- Phone: the tools below are on a second row behind this button; notifications stay in the bar. -->
        <button *ngIf="layout.isPhone()" type="button" class="tool tools-toggle" [class.on]="phoneToolsOpen"
                [attr.aria-expanded]="phoneToolsOpen" aria-label="More tools" (click)="phoneToolsOpen = !phoneToolsOpen">
          <mat-icon aria-hidden="true">more_vert</mat-icon>
        </button>
        <!-- data-page-finder-toggle: the finder's click-outside close leaves this button to toggle it. -->
        <button type="button" class="tool" data-page-finder-toggle [class.on]="pageFinder.isOpen()"
                aria-label="What are you looking for? Find an action on this page" aria-keyshortcuts="Control+/"
                [attr.aria-expanded]="pageFinder.isOpen()" matTooltip="Find on this page (Ctrl /)"
                (click)="pageFinder.toggle()">
          <mat-icon aria-hidden="true">manage_search</mat-icon>
        </button>
        <app-attendance-check-in></app-attendance-check-in>
        <!-- Who's in: colleagues marked Available / Away / Offline from the automatic check-in. -->
        <app-team-presence></app-team-presence>
        <a class="tool" [href]="phonebookUrl" (click)="onContactsClick($event)" (auxclick)="$event.stopPropagation()"
           aria-label="Contacts" matTooltip="Contacts (Ctrl-click for a new tab)">
          <mat-icon aria-hidden="true">contacts</mat-icon>
        </a>
        <button *ngIf="canSeeNotes$ | async" type="button" class="tool" (click)="openTaggedNotes($event)" [bcNavLink]="'/tagged-notes'"
                [attr.aria-label]="'Notes' + ((meetingNotesPanelService.unreadCount$ | async) ? ', ' + (meetingNotesPanelService.unreadCount$ | async) + ' unread' : '')"
                matTooltip="Notes">
          <mat-icon aria-hidden="true"
                    [matBadge]="(meetingNotesPanelService.unreadCount$ | async) > 0 ? (meetingNotesPanelService.unreadCount$ | async) : null"
                    matBadgeColor="warn" matBadgeSize="small" matBadgePosition="above after">assignment</mat-icon>
        </button>
        <button type="button" class="tool" [class.on]="outlookPanelService.isOpen" aria-label="Messages (Outlook)"
                matTooltip="Messages" (click)="toggleOutlookPanel(); $event.stopPropagation()">
          <mat-icon aria-hidden="true">mail_outline</mat-icon>
        </button>
        <!-- Feedback (report a bug / request a feature). It used to float over the page's own buttons. -->
        <app-report-widget></app-report-widget>
        <app-notification-dropdown
          [notifications]="notifications"
          [unreadCount]="unreadCount"
          [isAdmin]="isAdmin"
          [isLoading]="isLoading"
          [companies]="userCompanies || []"
          [currentCompanyId]="currentCompanyIdFromStorage"
          (markAsRead)="markAsRead($event.note, $event.index)"
          (markAllAsRead)="markAllAsRead()"
          (deleteNotification)="deleteNotification($event.note, $event.index)"
          (viewLeave)="viewPendingLeaves($event)"
          (viewAll)="navigateToNotifications()"
          (openSettings)="navigateToNotificationSettings()">
        </app-notification-dropdown>
      </div>
    </header>

    <main id="main" tabindex="-1">
      <ng-content></ng-content>
    </main>

    <!-- Phone bottom tabs (mockups/phone). "More" opens the drawer. -->
    <bc-tab-bar *ngIf="showTabBar" class="tabbar not-printable" [tabs]="phoneTabs" [moreActive]="moreTabActive"
                [moreExpanded]="isMobileMenuOpen" (more)="toggleMobileNav()"></bc-tab-bar>
  </div>
</div>

<!-- ============ Menus (overlays) ============ -->

<!-- Organisation: the header's company picker, same actions. -->
<mat-menu #companyMenu="matMenu" class="company-picker-menu bc-shell-menu">
  <div class="company-picker-filter" (click)="$event.stopPropagation()" (keydown)="$event.stopPropagation()">
    <label class="bc-sr-only" for="bc-org-filter">Find an organisation</label>
    <input id="bc-org-filter" class="company-picker-filter-input" #companyFilter placeholder="Find an organisation"
           type="text" (keyup)="onCompanySearch(companyFilter.value)">
  </div>
  <div class="company-picker-list">
    <div *ngFor="let company of filteredCompanies" class="company-picker-row"
         [class.current]="company.id === currentCompanyIdFromStorage" (click)="$event.stopPropagation()">
      <span class="company-picker-row-label" [class.clickable]="isAdmin"
            [matTooltip]="isAdmin ? 'Open this company in Organisation settings' : ''"
            (click)="openCompanySettingsInPlace(company.id, $event, companyMenuTrigger)">{{ company.name }}</span>
      <span class="company-picker-row-actions" *ngIf="company.id !== currentCompanyIdFromStorage">
        <button mat-icon-button [attr.aria-label]="'View ' + company.name + ' as a client in a new tab'"
                matTooltip="View as client (new tab)" (click)="openCompanyAsForm(company.id, $event)">
          <mat-icon>open_in_new</mat-icon>
        </button>
        <button *ngIf="isAdmin" mat-icon-button [attr.aria-label]="'Organisation settings for ' + company.name + ' in a new tab'"
                matTooltip="Organisation settings (new tab)" (click)="openCompanySettings(company.id, $event)">
          <mat-icon>settings</mat-icon>
        </button>
        <button mat-icon-button [attr.aria-label]="'Open ' + company.name + ' in a new tab'"
                matTooltip="Switch to this company (new tab)" (click)="switchToCompany(company.id, $event)">
          <mat-icon>swap_horiz</mat-icon>
        </button>
      </span>
      <mat-icon *ngIf="company.id === currentCompanyIdFromStorage" class="company-picker-current-icon" aria-label="Current organisation">check</mat-icon>
    </div>
  </div>
</mat-menu>

<!-- Top bar organisation crumb: Company Settings for this tab's organisation (admins only). -->
<mat-menu #orgSettingsMenu="matMenu" class="dash-layout-menu bc-shell-menu">
  <div class="dash-layout-head" (click)="$event.stopPropagation()">
    <span class="dash-layout-title">Company settings</span>
    <span class="dash-layout-count">Opens in a new tab</span>
  </div>
  <button mat-menu-item *ngFor="let section of companySettingsSections" (click)="openCurrentCompanySettings(section.step)">
    <mat-icon>{{ section.icon }}</mat-icon>
    <span>{{ section.label }}</span>
  </button>
  <button mat-menu-item class="dash-layout-reset" (click)="openCurrentCompanySettings()">
    <mat-icon>open_in_new</mat-icon>
    <span>All settings</span>
  </button>
</mat-menu>

<!-- Search scope: companies -->
<mat-menu #companiesScopeMenu="matMenu" class="search-companies-menu">
  <button mat-menu-item (click)="selectAllSearchCompanies(); $event.stopPropagation()">
    <mat-icon>{{ selectedSearchCompanyIds.length === 0 ? 'check_box' : 'check_box_outline_blank' }}</mat-icon>
    <span>All companies</span>
  </button>
  <mat-divider></mat-divider>
  <button mat-menu-item *ngFor="let company of userCompanies" (click)="toggleCompanyFilter(company.id); $event.stopPropagation()">
    <mat-icon>{{ isCompanyFilterActive(company.id) ? 'check_box' : 'check_box_outline_blank' }}</mat-icon>
    <span>{{ company.name }}</span>
  </button>
</mat-menu>

<!-- Create: the menu's existing create-form entries. -->
<mat-menu #createMenu="matMenu" class="bc-shell-menu">
  <a mat-menu-item *ngFor="let item of createItems" [href]="buildNavHref(item)" (click)="onNavChildClick(item, $event)">
    <mat-icon>{{ item.icon || 'add' }}</mat-icon>
    <span>{{ item.name }}</span>
  </a>
</mat-menu>

<!-- Account and settings: the header's settings panel entries. -->
<mat-menu #accountMenu="matMenu" yPosition="above" class="bc-shell-menu">
  <div class="account-head" *ngIf="currentUser" aria-hidden="true">
    <span class="account-name">{{ userDisplayName }}</span>
    <span class="account-email">{{ currentUser.email }}</span>
  </div>
  <a mat-menu-item routerLink="/settings/user"><mat-icon>person</mat-icon><span>Profile</span></a>
  <a mat-menu-item routerLink="/settings/notifications"><mat-icon>notifications</mat-icon><span>Notification settings</span></a>
  <a mat-menu-item routerLink="/settings/organization"><mat-icon>corporate_fare</mat-icon><span>Organisation settings</span></a>
  <a mat-menu-item *ngIf="canAdministerPermissions" routerLink="/settings/permissions">
    <mat-icon>admin_panel_settings</mat-icon><span>User permissions</span>
  </a>
  <ng-container *ngxPermissionsOnly="['CorporateAdmin']">
    <a mat-menu-item routerLink="/corporate/risk-settings"><mat-icon>assessment</mat-icon><span>Risk assessment</span></a>
    <a mat-menu-item routerLink="/admin/pending-changes"><mat-icon>rule</mat-icon><span>Pending changes</span></a>
  </ng-container>
  <a mat-menu-item routerLink="/drafts"><mat-icon>edit_note</mat-icon><span>My drafts</span></a>
  <mat-divider></mat-divider>
  <button mat-menu-item (click)="onLogout()"><mat-icon>logout</mat-icon><span>Log out</span></button>
</mat-menu>
